import React, { ReactElement } from 'react';
import { Button } from 'antd';
import { signOut } from '@triare/auth-redux/dist/saga/auth/signOut';
import { useDispatch } from 'react-redux';
import { useAuth, User } from '../store/auth';
import SignIn from '../pages/Auth/SignIn';
import { Route, RouteElements, RouteRoles } from './index';
import NotFound from '../pages/NotFound';
import { UserRole } from '../enums/user';
import TwoFASetupModal from '../components/Pages/Auth/TwoFASetupModal';

export function doesUserHaveAccess(roles?: RouteRoles, user?: User): boolean {
  if (roles && roles.length > 0 && user) {
    const roleListString = roles.filter((role) => typeof role === 'string') as string[];

    if (roleListString.length > 0 && roleListString.includes(user?.role)) {
      return true;
    }

    const roleListFn = roles.filter((role) => typeof role === 'function') as ((user: User) => boolean)[];

    if (roleListFn.length > 0 && roleListFn.some((role) => role(user))) {
      return true;
    }

    return false;
  }

  return true;
}

interface PrivateRouteProps {
  children?: ReactElement | React.ReactNode

  item?: Route

  element?: React.ReactNode

  elements?: RouteElements
}

function PrivateRoute({
  children, item, element, elements,
}: PrivateRouteProps): React.ReactNode | null | undefined {
  const {
    authorized, user,
    // Use these states, to display new modal to setup 2fa
    requiresOtpVerification,
    qrCode,
    secret,
    requiresSetup,
  } = useAuth();

  const dispatch = useDispatch();

  if (user && [UserRole.GUEST, UserRole.USER].includes(user.role as UserRole)) {
    return (
      <NotFound>
        <div style={{ display: 'flex', flexDirection: 'column', justifyItems: 'center' }}>
          <p>You not allowed to visit this portal</p>
          <Button onClick={() => dispatch(signOut())}>
            Sign out
          </Button>
        </div>
      </NotFound>
    );
  }

  if (user && item && item.roles) {
    let isBlocked = false;
    let roles;
    let route = item;

    while (route.roles) {
      roles = route.roles;

      if (!doesUserHaveAccess(roles, user)) {
        isBlocked = true;
        break;
      } else if (route.parent) {
        route = route.parent;
      } else {
        break;
      }
    }

    if (isBlocked) {
      return <NotFound />;
    }
  }

  if (
    !authorized
    && (!requiresSetup && !requiresOtpVerification)
  ) {
    return <SignIn />;
  }

  /** Handle 2FA Setup Interception Gate */
  // If the credentials matched but setup metadata indicates an action is required,
  // hold back rendering panel elements and show the setup Modal explicitly.
  if (requiresSetup || requiresOtpVerification) {
    return (
      <>
        <TwoFASetupModal
          visible={!!requiresSetup || !!requiresOtpVerification}
          qrCode={qrCode}
          secret={secret}
          // loading={setupLoading}
          // error={setupError}
        />
        {/* Background mask */}
        <div style={{ width: '100vw', height: '100vh', background: '#f0f2f5' }} />
      </>
    );
  }

  if (elements && user) {
    if (elements[user.role]) {
      return elements[user.role];
    }

    if (elements['*']) {
      return elements['*'];
    }

    return <NotFound />;
  }

  if (element) {
    return element;
  }

  return children;
}

export default PrivateRoute;
